import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  message,
  Typography,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { staffService, Staff } from '../services/staffs';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ResizableTableHeader } from '../components/ResizableTableHeader';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import '../styles/resizable-table.css';

const { Title } = Typography;

type FilterType = 'all' | 'active' | 'inactive';

export const Staffs: React.FC = () => {
  const [staffs, setStaffs] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canView = hasPermission(user, 'staff.view');
  const canCreate = hasPermission(user, 'staff.create');

  useEffect(() => {
    if (!canView) {
      setStaffs([]);
      return;
    }
    fetchData();
  }, [canView]);

  const fetchData = async () => {
    if (!canView) {
      return;
    }
    setLoading(true);
    try {
      const data = await staffService.getAll();
      setStaffs(Array.isArray(data) ? data : []);
    } catch {
      message.error('Failed to load staffs data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate('/staffs/new');
  };

  const handleEdit = (staff: Staff) => {
    if (!canView) {
      return;
    }
    navigate(`/staffs/${staff.id}/edit`);
  };

  const handleDelete = async (id: number) => {
    try {
      await staffService.delete(id);
      message.success('Staff deleted');
      fetchData();
    } catch {
      message.error('Failed to delete staff');
    }
  };

  const baseColumns = [
    { 
      title: 'Name', 
      dataIndex: 'name', 
      key: 'name', 
      width: 200,
      sorter: (a: Staff, b: Staff) => (a.name || '').localeCompare(b.name || ''),
    },
    { 
      title: 'Team', 
      dataIndex: 'team', 
      key: 'team', 
      width: 180,
      sorter: (a: Staff, b: Staff) => (a.team?.name || '').localeCompare(b.team?.name || ''),
      render: (team: Staff['team']) => team?.name || '-',
    },
    { 
      title: 'Role', 
      dataIndex: 'role', 
      key: 'role', 
      width: 180,
      sorter: (a: Staff, b: Staff) => (a.role?.name || '').localeCompare(b.role?.name || ''),
      render: (role: Staff['role']) => role?.name || '-',
    },
    { 
      title: 'Contact Number', 
      dataIndex: 'contact_number', 
      key: 'contact_number', 
      width: 180,
      sorter: (a: Staff, b: Staff) => (a.contact_number || '').localeCompare(b.contact_number || ''),
    },
    { 
      title: 'Email', 
      dataIndex: 'email', 
      key: 'email', 
      width: 250,
      sorter: (a: Staff, b: Staff) => (a.email || '').localeCompare(b.email || ''),
    },
  ];

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
  } = useColumnVisibility(baseColumns);

  const { columns: resizableColumns } = useResizableColumns(visibleColumns);

  const memoizedColumns = useMemo(
    () =>
      resizableColumns.map((col) => ({
        ...col,
        width: col.width, // Preserve width
        onHeaderCell: col.onHeaderCell, // Preserve onHeaderCell for resizing
        getSearchValue:
          col.dataIndex === 'name'
            ? (record: Staff) => record.name
            : col.dataIndex === 'team'
            ? (record: Staff) => record.team?.name || ''
            : col.dataIndex === 'role'
            ? (record: Staff) => record.role?.name || ''
            : col.dataIndex === 'contact_number'
            ? (record: Staff) => record.contact_number
            : col.dataIndex === 'email'
            ? (record: Staff) => record.email
            : (undefined as unknown as (record: Staff) => string | number | null | undefined),
      })),
    [resizableColumns]
  );

  const filteredStaffs = useMemo(() => {
    if (filter === 'all') {
      return staffs;
    } else if (filter === 'active') {
      return staffs.filter(staff => staff.type === 'active' || !staff.type);
    } else if (filter === 'inactive') {
      return staffs.filter(staff => staff.type === 'inactive');
    }
    return staffs;
  }, [staffs, filter]);

  const { columnsWithSearch, filteredData } = useTableColumnSearch<Staff>(filteredStaffs, memoizedColumns as any);

  return (
    <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            Staffs
          </Title>
          <Space>
            <ColumnVisibilityToggle
              columns={columnConfig}
              visibleKeys={visibleKeys}
              onChange={handleColumnVisibilityChange}
            />
          {canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              New Staff
            </Button>
          )}
        </Space>
      </div>

        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button 
              type={filter === 'all' ? 'primary' : 'default'}
              onClick={() => setFilter('all')}
            >
              All
            </Button>
            <Button 
              type={filter === 'active' ? 'primary' : 'default'}
              onClick={() => setFilter('active')}
            >
              Active
            </Button>
            <Button 
              type={filter === 'inactive' ? 'primary' : 'default'}
              onClick={() => setFilter('inactive')}
            >
              Inactive
          </Button>
          </Space>
        </div>

        <Table
          columns={columnsWithSearch}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ y: 'calc(100vh - 400px)' }}
          components={{
            header: {
              cell: ResizableTableHeader,
            },
          }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            hideSelectAll: true,
            columnWidth: 0,
            renderCell: () => null,
          }}
          footer={() => (
            <div style={{ textAlign: 'left', padding: '8px 0' }}>
              Total: {filteredData.length} {filteredData.length === 1 ? 'staff' : 'staffs'}
            </div>
          )}
          onRow={(record) => ({
            onClick: () => setSelectedRowKeys([record.id]),
            onDoubleClick: canView ? () => handleEdit(record) : undefined,
            style: { cursor: canView ? 'pointer' : 'default' },
          })}
        />
    </div>
  );
};
